import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import {
  IconChartPie,
  IconFilter,
  IconHistory,
  IconReceipt,
  IconReportMoney,
  IconTrendingDown,
  IconTrendingUp,
} from "@tabler/icons-react"
import { useState } from "react"
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Customized, Pie, PieChart, XAxis, YAxis } from "recharts"

interface Customer {
  id: number
  name: string
}

interface Props {
  [key: string]: unknown
  filters: {
    start_date: string
    end_date: string
    currency: string
    customer_id: string | null
  }
  customers: Customer[]
  currency_symbol: string
  metrics: {
    mrr: number
    mrc: number
    arr: number
    arc: number
    net_mrr: number
    net_arr: number
    margin_percentage: number
  }
  debt_metrics: {
    invoiced: number
    costed: number
    net: number
  }
  historic_metrics: {
    total_earned: number
    total_owed_to_us: number
    total_paid: number
    net_profit: number
  }
  charts: {
    income_by_customer: { name: string; amount: number }[]
    monthly_revenue_vs_costs: { month: string; revenue: number; costs: number }[]
    revenue_share: { name: string; value: number }[]
    invoices_by_status: Record<string, unknown>[]
    costs_by_status: Record<string, unknown>[]
    costs_by_type: { name: string; value: number }[]
  }
}

const PIE_COLORS = [
  "hsl(142, 71%, 45%)", // emerald
  "hsl(217, 91%, 60%)", // blue
  "hsl(263, 70%, 50%)", // violet
  "hsl(25, 95%, 53%)", // orange
  "hsl(340, 75%, 55%)", // pink
  "hsl(47, 96%, 53%)", // amber
  "hsl(173, 80%, 40%)", // teal
  "hsl(280, 65%, 60%)", // purple
  "hsl(0, 84%, 60%)", // red
  "hsl(199, 89%, 48%)", // sky
]

const revenueVsCostsConfig = {
  revenue: { label: "Revenue", color: "hsl(142, 71%, 45%)" },
  costs: { label: "Costs", color: "hsl(0, 84%, 60%)" },
} satisfies ChartConfig

const invoiceStatusConfig = {
  paid: { label: "Paid", color: "hsl(142, 71%, 45%)" },
  sent: { label: "Sent", color: "hsl(217, 91%, 60%)" },
  overdue: { label: "Overdue", color: "hsl(0, 84%, 60%)" },
  draft: { label: "Draft", color: "hsl(220, 9%, 46%)" },
  cancelled: { label: "Cancelled", color: "hsl(220, 9%, 70%)" },
} satisfies ChartConfig

const costStatusConfig = {
  paid: { label: "Paid", color: "hsl(142, 71%, 45%)" },
  pending: { label: "Pending", color: "hsl(47, 96%, 53%)" },
  overdue: { label: "Overdue", color: "hsl(0, 84%, 60%)" },
  draft: { label: "Draft", color: "hsl(220, 9%, 46%)" },
  cancelled: { label: "Cancelled", color: "hsl(220, 9%, 70%)" },
} satisfies ChartConfig

function AverageLine({ yAxisMap, offset, avg, label }: any) {
  if (!avg || !yAxisMap || !offset) return null
  const yAxis = yAxisMap[Object.keys(yAxisMap)[0]]
  if (!yAxis?.scale) return null
  const y = yAxis.scale(avg)
  if (y == null || Number.isNaN(y)) return null
  const x1 = offset.left
  const x2 = offset.left + offset.width
  return (
    <g>
      <line x1={x1} y1={y} x2={x2} y2={y} stroke="currentColor" strokeWidth={2} strokeDasharray="6 4" opacity={0.5} />
      <text x={x2 + 4} y={y + 4} fontSize={11} fill="currentColor" opacity={0.7}>
        {label}
      </text>
    </g>
  )
}

function fmt(amount: number, symbol: string): string {
  return `${symbol}${amount.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

function fmtFull(amount: number, symbol: string): string {
  return `${symbol}${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export default function RevenueDashboardIndex() {
  const { filters, customers, currency_symbol, metrics, debt_metrics, historic_metrics, charts } =
    usePage<Props>().props

  const [filterState, setFilterState] = useState({
    start_date: filters.start_date,
    end_date: filters.end_date,
    currency: filters.currency,
    customer_id: filters.customer_id || "",
  })

  function applyFilters() {
    const params: Record<string, string> = {
      start_date: filterState.start_date,
      end_date: filterState.end_date,
      currency: filterState.currency,
    }
    if (filterState.customer_id) {
      params.customer_id = filterState.customer_id
    }
    router.get("/admin/revenue_dashboard", params, { preserveState: true })
  }

  const marginPositive = metrics.margin_percentage >= 0
  const netPositive = debt_metrics.net >= 0
  const profitPositive = historic_metrics.net_profit >= 0

  // Build income chart config dynamically
  const incomeChartConfig: ChartConfig = { amount: { label: "Revenue", color: "hsl(142, 71%, 45%)" } }

  // Build revenue share config
  const revenueShareConfig: ChartConfig = {}
  charts.revenue_share.forEach((item, i) => {
    revenueShareConfig[item.name] = { label: item.name, color: PIE_COLORS[i % PIE_COLORS.length] }
  })

  // Build costs by type config
  const costTypeConfig: ChartConfig = {}
  charts.costs_by_type.forEach((item, i) => {
    costTypeConfig[item.name] = { label: item.name, color: PIE_COLORS[i % PIE_COLORS.length] }
  })

  // Revenue share total for percentage calculation
  const revenueShareTotal = charts.revenue_share.reduce((sum, item) => sum + item.value, 0)

  // Compute invoice average and inject into data rows
  const invoiceAvg =
    charts.invoices_by_status.length > 0
      ? (charts.invoices_by_status as Array<{ total: number }>).reduce((sum, r) => sum + (r.total || 0), 0) /
        charts.invoices_by_status.length
      : 0
  const invoiceStatusData = charts.invoices_by_status.map((row) => ({
    ...row,
    average: Math.round(invoiceAvg * 100) / 100,
  }))

  // Compute cost average and inject into data rows
  const costAvg =
    charts.costs_by_status.length > 0
      ? (charts.costs_by_status as Array<{ total: number }>).reduce((sum, r) => sum + (r.total || 0), 0) /
        charts.costs_by_status.length
      : 0
  const costStatusData = charts.costs_by_status.map((row) => ({ ...row, average: Math.round(costAvg * 100) / 100 }))

  return (
    <>
      <Head title="Revenue Dashboard - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        {/* Breadcrumb */}
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/revenue_dashboard">Finance</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Revenue Dashboard</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Header */}
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Revenue Dashboard</h1>
            <p className="mt-1 text-sm text-muted-foreground">Financial overview and analytics for your business</p>
          </div>
          <Badge variant={marginPositive ? "default" : "destructive"} className="h-8 gap-1.5 px-3 text-sm">
            {marginPositive ? <IconTrendingUp className="h-4 w-4" /> : <IconTrendingDown className="h-4 w-4" />}
            {metrics.margin_percentage.toFixed(1)}% Margin
          </Badge>
        </div>

        {/* Filters */}
        <Card className="gap-0 py-0">
          <CardContent className="px-6 py-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[140px]">
                <Label htmlFor="start_date" className="mb-1.5 text-xs">
                  Start Date
                </Label>
                <Input
                  id="start_date"
                  type="date"
                  value={filterState.start_date}
                  onChange={(e) => setFilterState((s) => ({ ...s, start_date: e.target.value }))}
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <Label htmlFor="end_date" className="mb-1.5 text-xs">
                  End Date
                </Label>
                <Input
                  id="end_date"
                  type="date"
                  value={filterState.end_date}
                  onChange={(e) => setFilterState((s) => ({ ...s, end_date: e.target.value }))}
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <Label className="mb-1.5 text-xs">Currency</Label>
                <Select
                  value={filterState.currency}
                  onValueChange={(v) => setFilterState((s) => ({ ...s, currency: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usd">USD ($)</SelectItem>
                    <SelectItem value="uyu">UYU ($U)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[160px]">
                <Label className="mb-1.5 text-xs">Customer</Label>
                <Select
                  value={filterState.customer_id || "all"}
                  onValueChange={(v) => setFilterState((s) => ({ ...s, customer_id: v === "all" ? "" : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Customers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Customers</SelectItem>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={applyFilters} size="sm" className="h-9">
                <IconFilter className="mr-1.5 h-4 w-4" />
                Apply
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* All-Time Historic Metrics */}
        <div className="rounded-xl border-2 border-blue-200/60 bg-blue-50/50 p-5 dark:border-blue-500/20 dark:bg-blue-950/20">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/15 dark:bg-blue-500/25">
              <IconHistory className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">All-Time Historic</h3>
              <p className="text-xs text-muted-foreground">Lifetime financial summary</p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <HistoricCard
              label="Total Earned"
              value={fmt(historic_metrics.total_earned, currency_symbol)}
              description="All paid invoices"
              color="emerald"
            />
            <HistoricCard
              label="They Owe Us"
              value={fmt(historic_metrics.total_owed_to_us, currency_symbol)}
              description="Unpaid invoices"
              color="amber"
            />
            <HistoricCard
              label="We Paid"
              value={fmt(historic_metrics.total_paid, currency_symbol)}
              description="All paid bills"
              color="red"
            />
            <HistoricCard
              label="Net Profit"
              value={fmt(historic_metrics.net_profit, currency_symbol)}
              description="Earned - Paid"
              color={profitPositive ? "emerald" : "red"}
              highlight
            />
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            icon={IconTrendingUp}
            label="MRR"
            sublabel="Monthly Recurring Revenue"
            value={fmtFull(metrics.mrr, currency_symbol)}
            footer={`Net: ${fmtFull(metrics.net_mrr, currency_symbol)}`}
            color="emerald"
          />
          <MetricCard
            icon={IconTrendingDown}
            label="MRC"
            sublabel="Monthly Recurring Cost"
            value={fmtFull(metrics.mrc, currency_symbol)}
            color="red"
          />
          <MetricCard
            icon={IconReportMoney}
            label="ARR"
            sublabel="Annual Recurring Revenue"
            value={fmtFull(metrics.arr, currency_symbol)}
            footer={`Net: ${fmtFull(metrics.net_arr, currency_symbol)}`}
            color="blue"
          />
          <MetricCard
            icon={IconReceipt}
            label="ARC"
            sublabel="Annual Recurring Cost"
            value={fmtFull(metrics.arc, currency_symbol)}
            color="violet"
          />
        </div>

        {/* Debt Metrics */}
        <div className="grid gap-4 sm:grid-cols-3">
          <Card className="gap-3 py-5">
            <CardContent className="px-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Invoiced</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-emerald-600 dark:text-emerald-400">
                {fmtFull(debt_metrics.invoiced, currency_symbol)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Total invoice value in period</p>
            </CardContent>
          </Card>
          <Card className="gap-3 py-5">
            <CardContent className="px-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Cost</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-red-600 dark:text-red-400">
                {fmtFull(debt_metrics.costed, currency_symbol)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Total bill value in period</p>
            </CardContent>
          </Card>
          <Card className="gap-3 py-5">
            <CardContent className="px-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Net Position</p>
              <p
                className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight ${netPositive ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}
              >
                {fmtFull(debt_metrics.net, currency_symbol)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Invoiced minus costs</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 1: Revenue vs Costs + Income by Customer */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Revenue vs Costs Area Chart */}
          <Card className="gap-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Revenue vs Costs</CardTitle>
              <CardDescription>Monthly comparison over time</CardDescription>
            </CardHeader>
            <CardContent className="px-2 pb-4 sm:px-4">
              {charts.monthly_revenue_vs_costs.length > 0 ? (
                <ChartContainer config={revenueVsCostsConfig} className="aspect-auto h-[300px] w-full">
                  <AreaChart data={charts.monthly_revenue_vs_costs} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="fillRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-revenue)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-revenue)" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="fillCosts" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-costs)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-costs)" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} fontSize={12} />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      fontSize={12}
                      tickFormatter={(v) => `${currency_symbol}${(v / 1000).toFixed(0)}k`}
                    />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          formatter={(value, name) => (
                            <div className="flex items-center justify-between gap-8">
                              <span className="text-muted-foreground">{name === "revenue" ? "Revenue" : "Costs"}</span>
                              <span className="font-mono font-medium tabular-nums">
                                {fmtFull(Number(value), currency_symbol)}
                              </span>
                            </div>
                          )}
                        />
                      }
                    />
                    <Area
                      dataKey="revenue"
                      type="monotone"
                      fill="url(#fillRevenue)"
                      stroke="var(--color-revenue)"
                      strokeWidth={2}
                    />
                    <Area
                      dataKey="costs"
                      type="monotone"
                      fill="url(#fillCosts)"
                      stroke="var(--color-costs)"
                      strokeWidth={2}
                    />
                    <ChartLegend content={<ChartLegendContent />} />
                  </AreaChart>
                </ChartContainer>
              ) : (
                <EmptyChart />
              )}
            </CardContent>
          </Card>

          {/* Income by Customer Bar Chart */}
          <Card className="gap-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Income by Customer</CardTitle>
              <CardDescription>Revenue breakdown per client</CardDescription>
            </CardHeader>
            <CardContent className="px-2 pb-4 sm:px-4">
              {charts.income_by_customer.length > 0 ? (
                <ChartContainer config={incomeChartConfig} className="aspect-auto h-[300px] w-full">
                  <BarChart
                    data={charts.income_by_customer}
                    layout="vertical"
                    margin={{ top: 0, right: 10, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid horizontal={false} strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      tickLine={false}
                      axisLine={false}
                      fontSize={12}
                      tickFormatter={(v) => `${currency_symbol}${(v / 1000).toFixed(0)}k`}
                    />
                    <YAxis
                      dataKey="name"
                      type="category"
                      tickLine={false}
                      axisLine={false}
                      fontSize={12}
                      width={120}
                      tickFormatter={(v) => (v.length > 16 ? `${v.slice(0, 14)}...` : v)}
                    />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          formatter={(value) => (
                            <span className="font-mono font-medium tabular-nums">
                              {fmtFull(Number(value), currency_symbol)}
                            </span>
                          )}
                        />
                      }
                    />
                    <Bar dataKey="amount" fill="hsl(142, 71%, 45%)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ChartContainer>
              ) : (
                <EmptyChart />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2: Revenue Share + Cost Breakdown */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Revenue Share Pie */}
          <Card className="gap-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Revenue Share</CardTitle>
              <CardDescription>Distribution across customers</CardDescription>
            </CardHeader>
            <CardContent className="overflow-hidden pb-4">
              {charts.revenue_share.length > 0 ? (
                <>
                  <ChartContainer
                    config={revenueShareConfig}
                    className="mx-auto aspect-square h-[280px] w-full max-w-[350px]"
                  >
                    <PieChart>
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            formatter={(value) => {
                              const pct =
                                revenueShareTotal > 0 ? ((Number(value) / revenueShareTotal) * 100).toFixed(1) : "0"
                              return (
                                <div className="flex items-center gap-2">
                                  <span className="font-mono font-medium tabular-nums">
                                    {fmtFull(Number(value), currency_symbol)}
                                  </span>
                                  <span className="text-muted-foreground">({pct}%)</span>
                                </div>
                              )
                            }}
                            nameKey="name"
                          />
                        }
                      />
                      <Pie
                        data={charts.revenue_share}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={100}
                        strokeWidth={2}
                        stroke="hsl(var(--background))"
                        label={({ cx: cxVal, cy: cyVal, midAngle, innerRadius: ir, outerRadius: or, value: val }) => {
                          const pct = revenueShareTotal > 0 ? (val / revenueShareTotal) * 100 : 0
                          if (pct < 5) return null
                          const RADIAN = Math.PI / 180
                          const radius = Number(ir) + (Number(or) - Number(ir)) * 0.5
                          const x = Number(cxVal) + radius * Math.cos(-midAngle * RADIAN)
                          const y = Number(cyVal) + radius * Math.sin(-midAngle * RADIAN)
                          return (
                            <text
                              x={x}
                              y={y}
                              textAnchor="middle"
                              dominantBaseline="central"
                              className="fill-white text-[11px] font-medium"
                            >
                              {pct.toFixed(0)}%
                            </text>
                          )
                        }}
                        labelLine={false}
                      >
                        {charts.revenue_share.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ChartContainer>
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5">
                    {charts.revenue_share.map((item, i) => {
                      const pct = revenueShareTotal > 0 ? ((item.value / revenueShareTotal) * 100).toFixed(1) : "0"
                      return (
                        <div key={item.name} className="flex items-center gap-1.5 text-xs">
                          <div
                            className="h-2 w-2 shrink-0 rounded-[2px]"
                            style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                          />
                          <span className="text-muted-foreground truncate max-w-[100px]">{item.name}</span>
                          <span className="tabular-nums font-medium">{pct}%</span>
                        </div>
                      )
                    })}
                  </div>
                </>
              ) : (
                <EmptyChart />
              )}
            </CardContent>
          </Card>

          {/* Cost Breakdown Donut */}
          <Card className="gap-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Cost Breakdown</CardTitle>
              <CardDescription>Expenses by category</CardDescription>
            </CardHeader>
            <CardContent className="overflow-hidden pb-4">
              {charts.costs_by_type.length > 0 ? (
                <>
                  <ChartContainer
                    config={costTypeConfig}
                    className="mx-auto aspect-square h-[280px] w-full max-w-[350px]"
                  >
                    <PieChart>
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            formatter={(value) => (
                              <span className="font-mono font-medium tabular-nums">
                                {fmtFull(Number(value), currency_symbol)}
                              </span>
                            )}
                            nameKey="name"
                          />
                        }
                      />
                      <Pie
                        data={charts.costs_by_type}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={100}
                        strokeWidth={2}
                        stroke="hsl(var(--background))"
                      >
                        {charts.costs_by_type.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ChartContainer>
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5">
                    {charts.costs_by_type.map((item, i) => {
                      const total = charts.costs_by_type.reduce((s, c) => s + c.value, 0)
                      const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0"
                      return (
                        <div key={item.name} className="flex items-center gap-1.5 text-xs">
                          <div
                            className="h-2 w-2 shrink-0 rounded-[2px]"
                            style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                          />
                          <span className="text-muted-foreground truncate max-w-[100px]">{item.name}</span>
                          <span className="tabular-nums font-medium">{pct}%</span>
                        </div>
                      )
                    })}
                  </div>
                </>
              ) : (
                <EmptyChart />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 3: Status Breakdowns */}
        <Tabs defaultValue="invoices" className="gap-4">
          <TabsList variant="line">
            <TabsTrigger value="invoices">Monthly Invoices by Status</TabsTrigger>
            <TabsTrigger value="costs">Monthly Costs by Status</TabsTrigger>
          </TabsList>

          <TabsContent value="invoices">
            <Card className="gap-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Invoice Status Breakdown</CardTitle>
                <CardDescription>
                  Stacked totals with average reference line ({fmtFull(invoiceAvg, currency_symbol)}/mo)
                </CardDescription>
              </CardHeader>
              <CardContent className="px-2 pb-4 sm:px-4">
                {invoiceStatusData.length > 0 ? (
                  <ChartContainer config={invoiceStatusConfig} className="aspect-auto h-[350px] w-full">
                    <BarChart data={invoiceStatusData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" />
                      <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} fontSize={12} />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        fontSize={12}
                        tickFormatter={(v) => `${currency_symbol}${(v / 1000).toFixed(0)}k`}
                      />
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            formatter={(value, name) => {
                              if (name === "average") return null
                              return (
                                <div className="flex items-center justify-between gap-8">
                                  <span className="capitalize text-muted-foreground">{String(name)}</span>
                                  <span className="font-mono font-medium tabular-nums">
                                    {fmtFull(Number(value), currency_symbol)}
                                  </span>
                                </div>
                              )
                            }}
                          />
                        }
                      />
                      <Bar dataKey="paid" stackId="a" fill="var(--color-paid)" />
                      <Bar dataKey="sent" stackId="a" fill="var(--color-sent)" />
                      <Bar dataKey="overdue" stackId="a" fill="var(--color-overdue)" />
                      <Bar dataKey="draft" stackId="a" fill="var(--color-draft)" />
                      <Bar dataKey="cancelled" stackId="a" fill="var(--color-cancelled)" radius={[2, 2, 0, 0]} />
                      <Customized
                        component={(props: any) => (
                          <AverageLine
                            {...props}
                            avg={invoiceAvg}
                            label={`Avg ${fmtFull(invoiceAvg, currency_symbol)}`}
                          />
                        )}
                      />
                      <ChartLegend content={<ChartLegendContent />} />
                    </BarChart>
                  </ChartContainer>
                ) : (
                  <EmptyChart />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="costs">
            <Card className="gap-0">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Cost Status Breakdown</CardTitle>
                <CardDescription>
                  Stacked totals with average reference line ({fmtFull(costAvg, currency_symbol)}/mo)
                </CardDescription>
              </CardHeader>
              <CardContent className="px-2 pb-4 sm:px-4">
                {costStatusData.length > 0 ? (
                  <ChartContainer config={costStatusConfig} className="aspect-auto h-[350px] w-full">
                    <BarChart data={costStatusData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" />
                      <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} fontSize={12} />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        fontSize={12}
                        tickFormatter={(v) => `${currency_symbol}${(v / 1000).toFixed(0)}k`}
                      />
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            formatter={(value, name) => {
                              if (name === "average") return null
                              return (
                                <div className="flex items-center justify-between gap-8">
                                  <span className="capitalize text-muted-foreground">{String(name)}</span>
                                  <span className="font-mono font-medium tabular-nums">
                                    {fmtFull(Number(value), currency_symbol)}
                                  </span>
                                </div>
                              )
                            }}
                          />
                        }
                      />
                      <Bar dataKey="paid" stackId="a" fill="var(--color-paid)" />
                      <Bar dataKey="pending" stackId="a" fill="var(--color-pending)" />
                      <Bar dataKey="overdue" stackId="a" fill="var(--color-overdue)" />
                      <Bar dataKey="draft" stackId="a" fill="var(--color-draft)" />
                      <Bar dataKey="cancelled" stackId="a" fill="var(--color-cancelled)" radius={[2, 2, 0, 0]} />
                      <Customized
                        component={(props: any) => (
                          <AverageLine {...props} avg={costAvg} label={`Avg ${fmtFull(costAvg, currency_symbol)}`} />
                        )}
                      />
                      <ChartLegend content={<ChartLegendContent />} />
                    </BarChart>
                  </ChartContainer>
                ) : (
                  <EmptyChart />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                      */
/* ------------------------------------------------------------------ */

function MetricCard({
  icon: Icon,
  label,
  sublabel,
  value,
  footer,
  color,
}: {
  icon: typeof IconTrendingUp
  label: string
  sublabel: string
  value: string
  footer?: string
  color: string
}) {
  const colorMap: Record<string, { bg: string; text: string }> = {
    emerald: { bg: "bg-emerald-500/15 dark:bg-emerald-500/20", text: "text-emerald-600 dark:text-emerald-400" },
    red: { bg: "bg-red-500/15 dark:bg-red-500/20", text: "text-red-600 dark:text-red-400" },
    blue: { bg: "bg-blue-500/15 dark:bg-blue-500/20", text: "text-blue-600 dark:text-blue-400" },
    violet: { bg: "bg-violet-500/15 dark:bg-violet-500/20", text: "text-violet-600 dark:text-violet-400" },
  }
  const c = colorMap[color] || colorMap.blue

  return (
    <Card className="gap-3 py-5">
      <CardContent className="px-5">
        <div className="flex items-center justify-between">
          <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${c.bg}`}>
            <Icon className={`h-4.5 w-4.5 ${c.text}`} />
          </div>
          <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">{label}</span>
        </div>
        <div className="mt-3">
          <p className="text-xs text-muted-foreground">{sublabel}</p>
          <p className="mt-0.5 text-2xl font-semibold tabular-nums tracking-tight">{value}</p>
        </div>
        {footer && <p className="mt-2 text-xs text-muted-foreground">{footer}</p>}
      </CardContent>
    </Card>
  )
}

function HistoricCard({
  label,
  value,
  description,
  color,
  highlight,
}: {
  label: string
  value: string
  description: string
  color: string
  highlight?: boolean
}) {
  const colorMap: Record<string, string> = {
    emerald: "text-emerald-700 dark:text-emerald-400",
    amber: "text-amber-700 dark:text-amber-400",
    red: "text-red-700 dark:text-red-400",
  }
  const textColor = colorMap[color] || colorMap.emerald

  return (
    <div
      className={`rounded-lg bg-white/80 p-4 shadow-sm dark:bg-card/80 ${highlight ? `ring-2 ring-offset-1 ring-offset-blue-50 dark:ring-offset-blue-950/20 ${color === "emerald" ? "ring-emerald-300 dark:ring-emerald-700" : "ring-red-300 dark:ring-red-700"}` : ""}`}
    >
      <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={`mt-1 text-xl font-bold tabular-nums ${textColor}`}>{value}</p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{description}</p>
    </div>
  )
}

function EmptyChart() {
  return (
    <div className="flex h-[300px] items-center justify-center">
      <div className="text-center">
        <IconChartPie className="mx-auto h-8 w-8 text-muted-foreground/40" />
        <p className="mt-2 text-sm text-muted-foreground">No data for the selected period</p>
      </div>
    </div>
  )
}

RevenueDashboardIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
